/*
  File: src/pages/OrdersPage.jsx
  Purpose: Display user's orders with lazy-loaded order items and product info.
  Responsibilities:
  - Fetch current user and list their orders; expand to fetch order items and product metadata.
  - Render collapsible order panels with status, date, and itemized lines.
*/
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faCircle, faCircleCheck, faTrash, faClipboardList, faCreditCard, faBoxOpen, faTruck, faTruckFast, faHouse, faCheckCircle, faXmark } from '@fortawesome/free-solid-svg-icons';
import Loader from '../components/common/Loader';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { apiClient, API } from '../auth/api';

export default function OrdersPage({ embedded = false }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({});
  const [userId, setUserId] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError('');
        // Get current user id
        const me = await apiClient.get('/users/me');
        const currentUserId = me?.data?.id;
        if (!currentUserId) { setOrders([]); return; }
        setUserId(currentUserId);
        const { data } = await apiClient.get('/orders', { params: { user_id: currentUserId, limit: 100, offset: 0 } });
        const list = data?.items || [];
        // Normalize dates
        setOrders(list);
      } catch (e) {
        setError('Failed to load orders');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch { return iso; }
  };

  const calcTotals = (order) => {
    const items = order?.items || [];
    if (!items.length) return { currency: 'USD', subtotal: 0 };
    const currency = items[0]?.currency || 'USD';
    const subtotalUnits = items.reduce((sum, it) => sum + Number(it.price_cents || 0) / 100, 0);
    return { currency, subtotal: subtotalUnits };
  };

  const buildCryptoQr = (order, payment) => {
    try {
      // If backend provided a payment URI, prefer it
      const uri = payment?.payment_uri || payment?.uri || '';
      if (uri) return uri;
      const address = payment?.payment_address || payment?.address || payment?.wallet_address || '';
      const amount = (payment?.amount_cents != null ? payment.amount_cents : (order?.amount_cents)) || null;
      const currency = payment?.currency || order?.currency || calcTotals(order?.id).currency || 'USD';
      const orderUid = order?.order_uid || `ORD-${order?.id || ''}`;
      // Construct a portable payload; unknown chain -> embed as plain text with fields
      const fields = {
        address,
        amount: amount ? (Number(amount) / 100).toString() : undefined,
        currency,
        order: orderUid,
      };
      const compact = Object.entries(fields)
        .filter(([, v]) => !!v)
        .map(([k, v]) => `${k}=${v}`)
        .join('&');
      return compact || address || orderUid;
    } catch { return `order=${order?.order_uid || order?.id || ''}`; }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {}
  };

  // Reset all orders for current user
  const resetOrders = async () => {
    if (!userId) return;
    
    try {
      setResetLoading(true);
      setResetMessage('');
      setShowResetModal(false);
      
      const response = await apiClient.delete(`${API}/orders/clear/${userId}`);
      const deletedCount = response.data?.deleted || 0;
      
      setResetMessage(`Successfully deleted ${deletedCount} orders`);
      setOrders([]); // Clear orders from UI
      
      // Clear the message after 3 seconds
      setTimeout(() => setResetMessage(''), 3000);
    } catch (e) {
      setResetMessage('Failed to reset orders');
      setTimeout(() => setResetMessage(''), 3000);
    } finally {
      setResetLoading(false);
    }
  };

  // Order lifecycle stages (common e-commerce flow)
  const orderLifecycle = [
    { 
      key: 'pending', 
      label: 'Pending', 
      description: 'Order received',
      icon: faClipboardList,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-500/20',
      borderColor: 'border-blue-500/50'
    },
    { 
      key: 'paid', 
      label: 'Paid', 
      description: 'Payment confirmed',
      icon: faCreditCard,
      color: 'from-indigo-500 to-blue-500',
      bgColor: 'bg-indigo-500/20',
      borderColor: 'border-indigo-500/50'
    },
    { 
      key: 'processing', 
      label: 'Processing', 
      description: 'Preparing order',
      icon: faBoxOpen,
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-500/20',
      borderColor: 'border-purple-500/50'
    },
    { 
      key: 'shipped', 
      label: 'Shipped', 
      description: 'In transit',
      icon: faTruck,
      color: 'from-orange-500 to-amber-500',
      bgColor: 'bg-orange-500/20',
      borderColor: 'border-orange-500/50'
    },
    { 
      key: 'out_for_delivery', 
      label: 'Out for Delivery', 
      description: 'Almost there',
      icon: faTruckFast,
      color: 'from-yellow-500 to-orange-500',
      bgColor: 'bg-yellow-500/20',
      borderColor: 'border-yellow-500/50'
    },
    { 
      key: 'delivered', 
      label: 'Delivered', 
      description: 'Package arrived',
      icon: faHouse,
      color: 'from-emerald-500 to-green-500',
      bgColor: 'bg-emerald-500/20',
      borderColor: 'border-emerald-500/50'
    },
    { 
      key: 'completed', 
      label: 'Completed', 
      description: 'Order finalized',
      icon: faCheckCircle,
      color: 'from-green-500 to-teal-500',
      bgColor: 'bg-green-500/20',
      borderColor: 'border-green-500/50'
    }
  ];

  // Get current stage index based on order status
  const getCurrentStage = (status) => {
    const normalized = String(status || 'pending').toLowerCase().replace(/[\s-_]/g, '_');
    
    // Map various status values to lifecycle stages
    if (normalized === 'cancelled' || normalized === 'canceled' || normalized === 'failed') {
      return -1; // Special case for cancelled/failed
    }
    
    // Map status to lifecycle stage index
    const statusMap = {
      'pending': 0,
      'paid': 1,
      'processing': 2,
      'shipped': 3,
      'out_for_delivery': 4,
      'outfordelivery': 4,
      'delivered': 5,
      'completed': 6
    };
    
    return statusMap[normalized] !== undefined ? statusMap[normalized] : 0;
  };

  // Render order lifecycle tracker
  const OrderLifecycleTracker = ({ status }) => {
    const currentStageIndex = getCurrentStage(status);
    const isCancelled = currentStageIndex === -1;

    if (isCancelled) {
      return (
        <div className="mb-6 p-5 rounded-2xl bg-gradient-to-br from-red-500/10 to-pink-500/10 border border-red-500/30 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center shadow-lg shadow-red-500/30">
              <FontAwesomeIcon icon={faXmark} className="text-white text-xl" />
            </div>
            <div>
              <div className="text-red-400 font-bold text-base">Order Cancelled</div>
              <p className="text-xs text-red-300/70 mt-0.5">This order has been cancelled and won't be processed</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="mb-6">
        {/* Modern Card-based Progress */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
          {orderLifecycle.map((stage, index) => {
            const isCompleted = index <= currentStageIndex;
            const isCurrent = index === currentStageIndex;
            
            return (
              <div 
                key={stage.key} 
                className={`relative rounded-xl p-4 border-2 transition-all duration-500 ${
                  isCompleted 
                    ? `bg-gradient-to-br ${stage.color} ${stage.borderColor} shadow-lg` 
                    : 'bg-white/5 border-white/10'
                }`}
              >
                {/* Icon */}
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 transition-all duration-300 ${
                  isCompleted 
                    ? `bg-white/20 backdrop-blur-sm ${isCurrent ? 'animate-pulse' : ''}` 
                    : 'bg-white/5'
                }`}>
                  <FontAwesomeIcon 
                    icon={stage.icon} 
                    className={`text-xl transition-all duration-300 ${
                      isCompleted ? 'text-white' : 'text-white/30'
                    } ${isCurrent ? 'scale-110' : ''}`}
                  />
                </div>
                
                {/* Status Badge */}
                {isCompleted && (
                  <div className="absolute top-2 right-2">
                    <div className="w-6 h-6 rounded-full bg-white/90 flex items-center justify-center">
                      <FontAwesomeIcon icon={faCircleCheck} className="text-green-600 text-xs" />
                    </div>
                  </div>
                )}
                
                {/* Label */}
                <div>
                  <div className={`text-xs sm:text-sm font-bold mb-1 transition-colors ${
                    isCompleted ? 'text-white' : 'text-white/40'
                  }`}>
                    {stage.label}
                  </div>
                  <div className={`text-[10px] sm:text-xs transition-colors ${
                    isCurrent ? 'text-white font-medium' : isCompleted ? 'text-white/80' : 'text-white/30'
                  }`}>
                    {stage.description}
                  </div>
                </div>
                
                {/* Current Indicator */}
                {isCurrent && (
                  <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2">
                    <div className="w-2 h-2 rounded-full bg-white animate-bounce shadow-lg shadow-white/50"></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Progress Bar Below */}
        <div className="mt-4 relative h-2 bg-white/5 rounded-full overflow-hidden">
          <div 
            className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 via-indigo-500 via-purple-500 via-orange-500 via-yellow-500 via-emerald-500 to-green-500 transition-all duration-700 ease-out rounded-full"
            style={{ width: `${((currentStageIndex + 1) / orderLifecycle.length) * 100}%` }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
          </div>
        </div>
        
        {/* Current Status Text */}
        <div className="mt-3 text-center">
          <span className="text-xs text-white/60">Current Status: </span>
          <span className={`text-sm font-bold bg-gradient-to-r ${orderLifecycle[currentStageIndex]?.color} bg-clip-text text-transparent`}>
            {orderLifecycle[currentStageIndex]?.label}
          </span>
        </div>
      </div>
    );
  };

  // Orders content component (reusable for embedded and standalone)
  const OrdersContent = () => (
    <div className={embedded ? "" : "max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8"}>
      <div className="flex items-center justify-between mb-6">
        {!embedded && <h1 className="text-2xl font-bold">My Orders</h1>}
        {embedded && <h2 className="text-xl font-bold">Order Management</h2>}
        {embedded && (
          <button
            onClick={() => setShowResetModal(true)}
            disabled={resetLoading || !userId || orders.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-red-600/80 hover:bg-red-600 disabled:bg-gray-600/50 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm font-medium"
          >
            <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
            {resetLoading ? 'Resetting...' : 'Reset Orders'}
          </button>
        )}
      </div>
      {resetMessage && (
        <div className={`mb-4 p-3 rounded-lg text-sm ${resetMessage.includes('Successfully') ? 'bg-green-600/20 text-green-300 border border-green-600/30' : 'bg-red-600/20 text-red-300 border border-red-600/30'}`}>
          {resetMessage}
        </div>
      )}
      {loading && (
        <div className="flex-1 flex items-center justify-center py-20"><Loader label="Loading orders" /></div>
      )}
      {error && !loading && <div className="text-red-400">{error}</div>}
      {!loading && orders.length === 0 && <div className="text-white/70">No orders yet.</div>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {orders.map(o => {
              const preview = (o.items || []).slice(0, 4);
              const lengthAll = (o.items || []).length || 0;
              const extra = Math.max(0, lengthAll - preview.length);
              return (
                <div key={o.id} className={`relative rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-2xl overflow-hidden ${expanded[o.id] ? 'md:col-span-3' : ''}`}>
                  <button
                    onClick={() => { setExpanded(e => ({ ...e, [o.id]: !e[o.id] })); }}
                    className="w-full text-left p-4 sm:p-5 flex items-center justify-between hover:bg-white/10 transition-colors gap-4"
                  >
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      {/* Thumbnails */}
                      <div className="hidden sm:flex items-center -space-x-2">
                        {preview.map((it, idx) => {
                          const thumbUrl = it?.variant?.image_url || it?.variant?.product?.image_url || it?.product?.image_url || null;
                          return (
                            <div key={idx} className="w-8 h-8 rounded-md border border-white/20 overflow-hidden bg-slate-600">
                              {thumbUrl ? <img src={thumbUrl} alt="" className="w-full h-full object-cover" /> : null}
                            </div>
                          );
                        })}
                        {extra > 0 && (
                          <div className="w-8 h-8 rounded-md border border-white/20 bg-white/10 text-white/80 text-[10px] flex items-center justify-center">+{extra}</div>
                        )}
                      </div>
                      {/* Order identifiers */}
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">Order #{o.id}</div>
                        <div className="text-xs text-white/70 truncate">{o.order_uid}</div>
                        {o.created_at && <div className="text-[11px] text-white/60">{formatDate(o.created_at)}</div>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border ${(o.status || 'pending') === 'completed'
                        ? 'border-emerald-400/30 bg-emerald-500/15 text-emerald-200'
                        : (o.status || 'pending') === 'pending'
                          ? 'border-yellow-400/30 bg-yellow-500/15 text-yellow-200'
                          : 'border-fuchsia-400/30 bg-fuchsia-500/15 text-fuchsia-100'
                        }`}>{o.status || 'pending'}</span>
                      <span className="w-7 h-7 inline-flex items-center justify-center rounded-md border border-white/20">{<FontAwesomeIcon icon={expanded[o.id] ? faChevronUp : faChevronDown} />}</span>
                    </div>
                  </button>
                  {expanded[o.id] && (
                    <div className="p-4 sm:p-5 border-t border-white/10 bg-white/[0.03]">
                      <div className="flex flex-col gap-4">
                        {/* Order Lifecycle Tracker */}
                        <OrderLifecycleTracker status={o.status} />
                        <div className="grid grid-cols-1 gap-3">
                          {(o.items || []).map(oi => (
                            <div key={oi.id} className="group flex items-center justify-between text-sm bg-white/5 rounded-xl px-3 py-3 gap-3 border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all">
                              <div className="flex items-center gap-4 min-w-0">
                                <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-slate-600 to-slate-700 overflow-hidden flex-shrink-0">
                                  {(() => {
                                    const imgUrl = oi?.variant?.image_url || oi?.variant?.product?.image_url || oi?.product?.image_url || null;
                                    const alt = oi?.variant?.product?.display_name || oi?.variant?.product?.name || oi?.product?.name || `Product #${oi.product_id}`;
                                    return imgUrl ? <img src={imgUrl} alt={alt} className="w-full h-full object-cover" /> : null;
                                  })()}
                                </div>
                                <div className="min-w-0">
                                  <div className="truncate text-white/90 font-medium">
                                    {oi?.variant?.product?.display_name || oi?.variant?.product?.name || oi?.product?.name || `Product #${oi.product_id}`}
                                  </div>
                                  <div className="text-white/60 text-[11px]">
                                    Qty {oi.qty}
                                    {oi?.variant?.sku ? <> • SKU {oi.variant.sku}</> : null}
                                    {oi?.variant?.size ? <> • Size {oi.variant.size}</> : null}
                                    {oi?.variant?.color ? <> • Color {oi.variant.color}</> : null}
                                  </div>
                                </div>
                              </div>
                              <div className="text-right whitespace-nowrap">
                                <div className="font-semibold">{oi.currency} {(Number(oi.price_cents || 0) / 100).toFixed(2)}</div>
                                <div className="text-white/60 text-[11px]">Line: {oi.currency} {((Number(oi.price_cents || 0) / 100) * (Number(oi.qty) || 1)).toFixed(2)}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-white/80">Subtotal</span>
                          <span className="font-semibold text-white">{calcTotals(o).currency} {calcTotals(o).subtotal.toFixed(2)}</span>
                        </div>
                        {/* Action buttons intentionally removed */}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
      </div>
    </div>
  );

  // Reset Confirmation Modal (only shown in embedded/dashboard context)
  const ResetModal = () => {
    if (!showResetModal || !embedded) return null;
    
    const modalContent = (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={() => setShowResetModal(false)}
        />
        
        {/* Modal */}
        <div className="relative bg-gray-900/95 backdrop-blur-xl border border-white/10 rounded-2xl p-6 max-w-md w-full shadow-2xl">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-red-500/20 border border-red-500/30 flex items-center justify-center">
              <FontAwesomeIcon icon={faTrash} className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Reset Orders</h3>
              <p className="text-sm text-white/60">Permanent action</p>
            </div>
          </div>
          
          {/* Content */}
          <div className="mb-6">
            <p className="text-white/80 mb-3">
              Are you sure you want to delete all your orders? This action will permanently remove:
            </p>
            <ul className="text-sm text-white/70 space-y-1 ml-4">
              <li>• All {orders.length} orders from your account</li>
              <li>• Order history and tracking information</li>
              <li>• Related order items and variants</li>
            </ul>
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-300 text-sm font-medium">
                ⚠️ This action cannot be undone
              </p>
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => setShowResetModal(false)}
              className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white border border-white/20 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={resetOrders}
              className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors font-medium flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
              Delete All Orders
            </button>
          </div>
        </div>
      </div>
    );

    // Render modal using portal to document.body for proper centering
    return createPortal(modalContent, document.body);
  };

  // Return appropriate layout based on embedded prop
  if (embedded) {
    return (
      <>
        <OrdersContent />
        <ResetModal />
      </>
    );
  }

  return (
    <div className="min-h-screen flex flex-col pt-14 sm:pt-16">
      <Navbar />
      <main className="flex-1 w-full">
        <OrdersContent />
      </main>
      <Footer />
    </div>
  );
}
